<template>
	<v-card class="sgnl-flex sgnl-flex-column sgnl-justify-space-between panel-card">
		<!-- Tab Header-->
		<div
			v-if="showTabs"
			class="panel-tab-header"
		>
			<v-tabs
				v-model="tab"
				height="auto"
			>
				<v-tab
					v-for="(stream, i) in resultText"
					:key="stream.code"
					:disabled="stream.disabled"
					:data-test="`${stream.code}-output-tab-header`"
					class="sgnl-paragraph-s-medium"
				>
					<component
						v-if="typeof stream.icon === 'object'"
						:is="stream.icon"
					/>
					<v-icon
						v-else
						:icon="stream.icon"
					/>
					<template v-if="stream.code_tag">
						<code class="inline-code">{{ stream.name }}</code>
					</template>
					<template v-else>
						<span>{{ stream.name }}</span>
					</template>
					<div
						v-if="tabBadgeState[i]"
						class="tab-badge-dot"
					/>
				</v-tab>
			</v-tabs>
		</div>
		<!-- Tab Content -->
		<!-- File storage -->
		<div class="sgnl-flex-grow-1 panel-content">
			<template v-if="tab == 5">
				<v-row
					class="sgnl-mb-20"
					align="center"
					justify="start"
				>
					<v-col>
						<SigCloudStorageFileList
							:showTitle="false"
							:flatCard="true"
							minWidth="100%"
							:inEditor="true"
						/>
					</v-col>
				</v-row>
			</template>
			<template v-else>
				<v-progress-linear
					v-if="isLoading || chunkIsLoading"
					indeterminate
				/>
				<div
					class="sgnl-p-12 results-div"
					ref="resultsContainer"
					:id="`results-sheet-id-${tab}`"
					:data-test="`${resultText[tab].code.toLowerCase()}-output-tab-text`"
				>
					<span
						v-for="(elem, index) in transformedContents"
						:key="index"
					>
						<!-- Image Display-->
						<template v-if="elem.type === 'image'">
							<!-- Hover Target -->
							<div
								class="plot-digit plot-digit-link sgnl-text-color-brand"
								@click="openPlotDialog(elem.body)"
								@mouseenter="$vuetify.display.mobile ? null : onHoverEnter(elem.body, $event)"
								@mouseleave="onHoverLeave()"
							>
								<ChartHistogram16
									v-if="!elem.body.Value || elem.body.Value.length === 0"
									class="plot-digit-icon"
								/>
								<span
									:class="autoPlotActive ? 'plot-digit-value' : 'plot-digit-value-dotted'"
									data-test="result-digit-value"
									>{{ elem.body.Value }}</span
								>
							</div>
							<LazyFigure
								v-if="autoPlotActive"
								v-model:imageURL="elem.body.ImageURL"
								:taskID="taskID"
								:valueID="elem.body.ValueID"
								:uxString="elem.body.UxString"
								imageMaxWidth="25em"
								class="panel-plot"
							/>
						</template>
						<!-- Text Display-->
						<div
							v-else
							class="panel-text sgnl-text-color-default"
							v-text="elem.body"
						/>
					</span>
					<FetchErrorBanner
						v-if="resultText[tab].code === 'Stdout' && stdoutChunkAccessState?.error"
						class="chunk-fetch-error"
						:accessState="stdoutChunkAccessState"
						@retry="retryChunkFetch"
					/>
					<Teleport to="body">
						<div
							v-if="hoveredItem"
							class="hover-overlay"
							:style="{
								top: overlayTop + 'px',
								left: overlayLeft + 'px',
								transform: overlayAbove ? 'translateY(-100%)' : 'none',
							}"
						>
							<v-card min-width="20em">
								<v-card-text class="sgnl-py-0 sgnl-px-0 hover-plot-body">
									<LazyFigure
										v-if="hoveredItem"
										:useIntersection="false"
										v-model:imageURL="hoveredItem.ImageURL"
										:taskID="taskID"
										:valueID="hoveredItem.ValueID"
										:uxString="hoveredItem.UxString"
										imageMaxHeight="var(--sgnl-container-343)"
										imageMaxWidth="var(--sgnl-container-480)"
									/>
								</v-card-text>
							</v-card>
						</div>
					</Teleport>
				</div>
			</template>
		</div>
		<!-- Buttons Row -->
		<div
			v-if="showFooter && tab != 5"
			class="panel-buttons-row sgnl-flex sgnl-gap-8"
		>
			<v-checkbox
				v-if="['Stdout', 'Stderr'].includes(resultText[tab].code)"
				v-model="autoPlotEnabled"
				@update:modelValue="toggleAutoplot"
				label="Show Plots"
				hide-details
			/>
			<v-spacer />
			<TooltipButton
				v-if="showClearButton"
				variant="outlined"
				width="auto"
				@click="requestClearTextArea"
				tooltipText="Clear Output"
			>
				<template #btnContent>
					<TrashCan16 />
					<span>Clear</span>
				</template>
			</TooltipButton>
			<TooltipButton
				v-if="showCopyButton"
				variant="outlined"
				width="auto"
				@click="copyOutputToClipboard(tab)"
				tooltipText="Copy Output"
			>
				<template #btnContent>
					<Copy16 />
					<span>Copy</span>
				</template>
			</TooltipButton>
			<v-snackbar
				v-model="showCopyNotification"
				class="snackbar-success"
			>
				<div class="snackbar-icon-container snackbar-success">
					<CheckmarkFilled16 />
				</div>
				Copied to clipboard!
			</v-snackbar>
		</div>
		<v-dialog
			v-model="showPlotDialog"
			:width="$vuetify.display.mobile ? '95%' : '500px'"
			@click:outside="closePlotDialog"
			@keydown.esc="closePlotDialog"
		>
			<v-card min-height="5em">
				<v-card-title class="dialog-header sgnl-subtitle-medium">
					<span>Plot</span>
					<v-spacer />
					<v-btn
						icon
						variant="text"
						size="small"
						@click="closePlotDialog"
					>
						<Close16 />
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="dialog-body">
					<LazyFigure
						:useIntersection="false"
						v-if="showPlotDialog && dataForPlotDialog"
						v-model:imageURL="dataForPlotDialog.ImageURL"
						:taskID="taskID"
						:valueID="dataForPlotDialog?.ValueID"
						:uxString="dataForPlotDialog.UxString"
						data-test="result-plot-figure"
					/>
				</v-card-text>
				<v-divider />
				<v-card-actions class="dialog-footer">
					<v-btn
						variant="outlined"
						@click="copyPlotValueToClipboard"
					>
						<Copy16 />
						Copy
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script lang="ts">
/* eslint-disable vue/multi-word-component-names */
import { defineComponent, PropType } from "vue";
// Types

// Libraries
import ClipboardJS from "clipboard";

// Utilities
import { downloadFile } from "@/components/DataSources/utilities";

// Components
import SigCloudStorageFileList from "@/components/DataSources/SigCloudStorageFileList.vue";
import LazyFigure from "@/components/DevelopmentPlatform/LazyFigure.vue";
import TooltipButton from "@/components/Common/TooltipButton.vue";
import FetchErrorBanner from "@/components/Common/FetchErrorBanner.vue";

import { ImageContent, RemoteAccessState, ResultsPanelTab, TransformedTabContent } from "@/types/general";
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark--filled/16";
import Close16 from "@carbon/icons-vue/es/close/16";
import Copy16 from "@carbon/icons-vue/es/copy/16";
import ChartHistogram16 from "@carbon/icons-vue/es/chart--histogram/16";
import TrashCan16 from "@carbon/icons-vue/es/trash-can/16";
import { mapStores } from "pinia";
import { useOutputsStore } from "@/stores/outputs";
import { defaultTabs } from "@/assets/defaultTabs";
import { transformTabContent, parseBuildError, ParsedBuildError } from "@/js/utilities";
import { useTasksStore } from "@/stores/tasks";
import { useBuildsStore } from "@/stores/builds";
import { Build, BuildStatusE } from "@/types/api/builds";
import { shouldSkipBuildOutputForError } from "@/types/buildErrors";

// Stores

// Global variables

// Local types
type ComponentData = {
	tab: number;
	newContent: boolean;
	tabBadges: boolean[];
	clipboard: null | any;
	showCopyNotification: boolean;
	autoPlotEnabled: undefined | boolean;
	showPlotDialog: boolean;
	dataForPlotDialog: undefined | ImageContent["body"];
	hoveredItem: null | ImageContent["body"];
	overlayTop: number;
	overlayLeft: number;
	overlayAbove: boolean;
	plotInDialogLoading: boolean;
	prefetchObjectThreshold: number;
	chunkIsLoading: boolean;
	tabBadgeState: boolean[];
	debounceTimer: ReturnType<typeof setTimeout>;
};

export default defineComponent({
	name: "Panel",
	components: {
		SigCloudStorageFileList,
		LazyFigure,
		TooltipButton,
		FetchErrorBanner,
		CheckmarkFilled16,
		Close16,
		Copy16,
		ChartHistogram16,
		TrashCan16,
	},
	emits: ["panel-cleared"],
	props: {
		resultText: {
			type: Array as PropType<ResultsPanelTab[]>,
			default: () => [...defaultTabs], // Use a function to return the default value
		},
		resultsTextAreaMinHeight: { type: String, default: "16vh" },
		resultsTextAreaMaxHeight: { type: String, default: "100%" },
		showCopyButton: { type: Boolean, default: false },
		showClearButton: { type: Boolean, default: false },
		showTabs: { type: Boolean, default: true },
		showFooter: { type: Boolean, default: true },
		forcePlots: { type: Boolean, default: false },
		taskID: { type: String, required: false, default: undefined },
		buildID: { type: String, required: false, default: undefined },
		resultsLoading: { type: Boolean, default: false },
	},
	data: (): ComponentData => ({
		tab: 0,
		newContent: false,
		tabBadges: [],
		clipboard: null,
		showCopyNotification: false,
		autoPlotEnabled: undefined,
		showPlotDialog: false,
		dataForPlotDialog: undefined,
		hoveredItem: null,
		overlayTop: 0,
		overlayLeft: 0,
		overlayAbove: false,
		plotInDialogLoading: false,
		prefetchObjectThreshold: 15,
		chunkIsLoading: false,
		tabBadgeState: Array(defaultTabs.length).fill(false),
		debounceTimer: setTimeout(() => {}, 0),
	}),
	watch: {
		taskID(newVal, oldVal) {
			if (newVal !== oldVal) {
				// Unkeyed panels swap tasks in place, so the hold follows the shown task.
				if (oldVal) {
					void this.outputsStore.releaseTask(oldVal);
				}
				this.outputsStore.retainTask(newVal ?? "");
				this.tabBadgeState = Array(defaultTabs.length).fill(false);
			}
		},
		buildID(newVal, oldVal) {
			if (newVal !== oldVal) {
				this.tabBadgeState = Array(defaultTabs.length).fill(false);
			}
		},
		tab(newVal) {
			this.tabBadgeState[newVal] = false;
		},
		storeStdout: "updateActiveTab",
		storeStderr: "updateActiveTab",
		storeBuild: "updateActiveTab",
		storeStats: "updateActiveTab",
	},
	methods: {
		updateActiveTab() {
			const newTab = this.findFirstNonEmptyTab();
			clearTimeout(this.debounceTimer);
			this.debounceTimer = setTimeout(() => {
				if (this.tab !== newTab) {
					this.tab = newTab;
				}

				this.resultText.forEach((tab, i) => {
					if (i > newTab && !tab.disabled) {
						const hasContent =
							(tab.code === "Stdout" && this.storeStdout.length !== 0) ||
							(tab.code === "Stderr" && this.storeStderr.length !== 0) ||
							(tab.code === "Build" && this.storeBuild.length !== 0) ||
							(tab.code === "Stats" && this.storeStats.length !== 0);

						if (hasContent) {
							this.tabBadgeState[i] = true;
						}
					}
				});
			}, 300);
		},
		onHoverEnter(item, event) {
			if (this.showPlotDialog || this.autoPlotActive) return;
			this.hoveredItem = item;
			const rect = event.currentTarget.getBoundingClientRect();
			const screenHeight = window.innerHeight;
			const screenWidth = window.innerWidth;
			const isLowerHalf = rect.top > screenHeight / 2;
			const isRightHalf = rect.left > screenWidth / 2;

			this.overlayAbove = isLowerHalf;
			if (isLowerHalf) {
				this.overlayTop = rect.top;
			} else {
				this.overlayTop = rect.bottom;
			}

			if (isRightHalf) {
				this.overlayLeft = rect.right - 320;
			} else {
				this.overlayLeft = rect.left;
			}
		},
		onHoverLeave() {
			this.hoveredItem = null;
			this.overlayLeft = 0;
			this.overlayTop = 0;
		},

		requestClearTextArea() {
			/*
			 *  requestClearTextArea needs to be a method to have the 'this' context.
			 */
			this.$emit("panel-cleared");
		},
		copyOutputToClipboard(selectedTabIndex: number) {
			const textToCopy = document.getElementById(`results-sheet-id-${selectedTabIndex}`)?.innerText;
			if (textToCopy) {
				navigator.clipboard.writeText(textToCopy);
				this.showCopyNotification = true;
			}
		},
		copyToClipboard(selectedTabIndex: number) {
			// @ts-ignore FIXME:
			const textarea = this.$refs[`textarea-${selectedTabIndex}`].$el.querySelector("textarea");
			textarea.select();
			textarea.setSelectionRange(0, 99999);
			document.execCommand("copy");
			window.getSelection()?.removeAllRanges();
		},
		openPlotDialog(data: ImageContent["body"]) {
			this.dataForPlotDialog = data;
			this.showPlotDialog = true;
		},
		closePlotDialog() {
			this.showPlotDialog = false;
			this.dataForPlotDialog = undefined;
		},
		copyPlotValueToClipboard() {
			if (this.dataForPlotDialog?.Value) {
				navigator.clipboard.writeText(this.dataForPlotDialog.Value);
				this.showCopyNotification = true;
			}
		},
		downloadFile(filePath: string) {
			downloadFile(filePath, "plot.png");
		},
		toggleAutoplot() {
			localStorage.setItem("resultsPanel-autoplot", (this.autoPlotEnabled ?? false).toString());
		},
		async fetchData() {
			if (!this.taskID || !this.hasMoreChunks || this.chunkIsLoading) return;

			this.chunkIsLoading = true;
			try {
				await this.outputsStore.fetchNextChunk(this.taskID);
			} catch {
				// stdoutChunkAccessState populated in store; inline retry row surfaces it.
			}

			this.chunkIsLoading = false;
		},
		async retryChunkFetch() {
			if (!this.taskID) return;
			try {
				await this.outputsStore.fetchNextChunk(this.taskID);
			} catch {
				// Error state re-populated by the store.
			}
		},
		handleScroll() {
			const container = this.$refs.resultsContainer as HTMLElement;

			if (!container) return;

			const threshold = container.scrollHeight * 0.7;
			const scrollPosition = container.scrollTop + container.clientHeight;
			if (scrollPosition >= threshold && !this.chunkIsLoading && !this.isLoading) {
				this.fetchData();
			}
		},
		async checkAndFetchMoreData() {
			await this.$nextTick();

			const container = this.$refs.resultsContainer;
			if (container instanceof HTMLElement) {
				if (container.scrollHeight > container.clientHeight || !this.hasMoreChunks) {
					return;
				}

				await this.fetchData();
				setTimeout(() => {
					this.checkAndFetchMoreData();
				}, 100);
			}
		},
		findFirstNonEmptyTab() {
			for (let i = 0; i < this.resultText.length; i++) {
				const code = this.resultText[i].code;
				if (!this.resultText[i].disabled) {
					if (
						(code === "Stdout" && this.storeStdout.length !== 0) ||
						(code === "Stderr" && this.storeStderr.length !== 0) ||
						(code === "Build" && this.storeBuild.length !== 0) ||
						(code === "Stats" && this.storeStats.length !== 0)
					) {
						return i;
					}
				}
			}
			return 0; // Default to first tab if all are empty
		},
	},
	computed: {
		...mapStores(useOutputsStore, useTasksStore, useBuildsStore),
		stdoutChunkAccessState(): RemoteAccessState | undefined {
			const outputsStore = useOutputsStore();
			return outputsStore.getStdoutChunkAccessState(this.taskID);
		},
		currentBuild(): Build | null {
			const buildID = this.$props.buildID ?? "";
			if (!buildID) return null;
			return this.buildsStore.getBuildByID(buildID);
		},
		buildError(): ParsedBuildError {
			const build = this.currentBuild;
			if (!build) {
				return { code: null, message: null };
			}
			return parseBuildError(build.StatusTransitions, build.Status);
		},
		shouldSkipBuildOutputLoading(): boolean {
			const build = this.currentBuild;
			const errorCode = this.buildError.code;
			const errorMessage = this.buildError.message;

			// Use centralized error checking first
			if (shouldSkipBuildOutputForError(errorCode)) {
				return true;
			}

			// Check if build was cancelled
			if (build?.Status === BuildStatusE.Cancelled) {
				return true;
			}

			// Fall back to message content check
			return errorMessage ? errorMessage.toLowerCase().includes("was cancelled by owner") : false;
		},
		storeStdout() {
			const taskOutputsStore = useOutputsStore();
			const taskID = this.taskID ?? "";
			const stringStdout = taskOutputsStore.getStringStdout(taskID);
			return transformTabContent(stringStdout, taskOutputsStore.getStdoutHasMoreChunks(taskID));
		},
		storeStderr() {
			const taskOutputsStore = useOutputsStore();
			const taskID = this.taskID ?? "";
			const stringStdout = taskOutputsStore.getStringStderr(taskID);
			return transformTabContent(stringStdout);
		},
		storeBuild(): TransformedTabContent[] {
			// If there's a repository clone error or build was cancelled, show the error message instead of loading from API
			const errorMessage = this.buildError.message;
			if (this.shouldSkipBuildOutputLoading && errorMessage) {
				return transformTabContent(errorMessage);
			}
			const outputsStore = useOutputsStore();
			const buildID = this.$props.buildID ?? "";
			const stringStdout = outputsStore.getStringBuild(buildID);
			return transformTabContent(stringStdout);
		},
		storeStats() {
			const taskOutputsStore = useOutputsStore();
			const taskID = this.taskID ?? "";
			const stringStdout = taskOutputsStore.getStringStats(taskID);
			return transformTabContent(stringStdout);
		},
		isLoading() {
			const outputsStore = useOutputsStore();
			const taskID = this.taskID ?? "";
			const buildID = this.buildID ?? "";
			const isBuildLoading = outputsStore.getBuildLoadingState(buildID);
			const isTaskLoading = outputsStore.getTaskLoadingState(taskID);
			// TODO: Check if this is playing well with the rest
			return isBuildLoading && isTaskLoading;
		},
		hasMoreChunks(): Boolean {
			const code = this.resultText[this.tab].code;
			if (code == "Stdout") {
				const store = useOutputsStore();
				const taskID = this.taskID ?? "";
				return store.getStdoutHasMoreChunks(taskID);
			}
			// We support only stdout chunks.
			return false;
		},
		transformedContents(): TransformedTabContent[] {
			const code = this.resultText[this.tab].code;
			if (code == "Stdout") {
				return this.storeStdout;
			}
			if (code == "Stderr") {
				return this.storeStderr;
			}
			if (code == "Build") {
				return this.storeBuild;
			}
			if (code == "Stats") {
				return this.storeStats;
			}
			return [];
		},
		tabBadge(): Boolean {
			const code = this.resultText[this.tab].code;
			if (code == "Stdout") {
				return this.storeStdout.length != 0;
			}
			if (code == "Stderr") {
				return this.storeStderr.length != 0;
			}
			if (code == "Build") {
				return this.storeBuild.length != 0;
			}
			if (code == "Stats") {
				return this.storeStats.length != 0;
			}
			return false;
		},
		autoPlotActive(): boolean {
			return this.forcePlots || (this.autoPlotEnabled ?? false);
		},
	},
	created() {
		// Not in `mounted`, which can bail early before every retain gets its release.
		this.outputsStore.retainTask(this.$props.taskID ?? "");
	},
	async mounted() {
		this.clipboard = new ClipboardJS(".copy-btn");
		this.$nextTick(() => {
			const container = this.$refs.resultsContainer;
			if (container instanceof HTMLElement) {
				container.addEventListener("scroll", this.handleScroll);
				this.fetchData();
			} else {
				console.error("Expected an HTMLElement but got:", container);
			}
		});
		const savedAutoPlot = (localStorage.getItem("resultsPanel-autoplot") ?? "false") === "true";
		localStorage.setItem("resultsPanel-autoplot", savedAutoPlot.toString());
		this.autoPlotEnabled = savedAutoPlot;
		// To eliminate fetching data when code editor has no active tasks.
		if (!this.taskID) {
			// Load build output when build fails and we did not trigger a task
			// First, fetch the build to check for errors
			if (this.$props.buildID) {
				try {
					await this.buildsStore.getBuildByIdOrFetch(this.$props.buildID);
				} catch {
					return;
				}
			}

			// Check if we should skip loading outputs (repository clone error or cancelled build)
			if (this.shouldSkipBuildOutputLoading) {
				this.updateActiveTab();
				return;
			}

			// Otherwise, try to load build outputs from API
			try {
				await this.outputsStore.loadBuildOutputs(this.$props.buildID ?? "");
				this.updateActiveTab();
			} catch {
				// buildOutputAccessState populated in store; BuildBar surfaces.
			}
			return;
		}
		try {
			const task = await this.tasksStore.getTaskByIdOrFetch(this.taskID);

			// Fetch the build first to check for errors
			if (task?.BuildID) {
				await this.buildsStore.getBuildByIdOrFetch(task.BuildID);
			}

			// Only load build outputs if we shouldn't skip (not a repository clone error or cancelled build)
			if (!this.shouldSkipBuildOutputLoading) {
				await this.outputsStore.loadBuildOutputs(task?.BuildID ?? "");
			}
			await this.outputsStore.loadTaskOutputs(this.$props.taskID ?? "");
		} catch {
			// Access state populated in store for output fetch errors; BuildBar surfaces.
			return;
		}
		this.updateActiveTab();
		await this.checkAndFetchMoreData();
	},
	async beforeUnmount() {
		this.clipboard?.destroy();
		const container = this.$refs.resultsContainer as HTMLElement;
		container?.removeEventListener("scroll", this.handleScroll);
		await this.outputsStore.releaseTask(this.$props.taskID ?? "");
	},
});
</script>

<style lang="scss" scoped>
.panel-card {
	height: 100%;
	overflow: hidden;
}

.panel-tab-header {
	border-bottom: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	flex-shrink: 0;
}

.panel-tab-header :deep(.v-slide-group__prev),
.panel-tab-header :deep(.v-slide-group__next) {
	min-width: var(--sgnl-space-24);
	flex: 0 0 var(--sgnl-space-24);
}

.panel-content {
	background-color: var(--sgnl-default-secondary-background-mono);
	min-height: 120px;
	overflow-y: auto;
}

.panel-buttons-row {
	border-top: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	flex-shrink: 0;
	align-items: center;
	padding: var(--sgnl-space-8) var(--sgnl-space-16);
}

.chunk-fetch-error {
	margin: var(--sgnl-space-8) 0;
	border: var(--sgnl-border-width-1) solid var(--sgnl-alert-error-border);
}

@media (max-width: 599px) {
	.panel-buttons-row {
		flex-direction: column;
		align-items: flex-start;
	}

	.panel-buttons-row :deep(.v-spacer) {
		display: none;
	}

	.panel-buttons-row :deep(.v-selection-control) {
		padding-inline-start: 0;
	}
}

.inline-code {
	font-family: var(--sgnl-font-family-mono);
	font-size: var(--sgnl-font-size-paragraph-s);
}

.results-div {
	font-family: var(--sgnl-font-family-mono);
	font-size: var(--sgnl-font-size-paragraph-s);
	height: 100%;
	overflow-x: auto;
	overflow-y: auto;
	white-space: pre-wrap;
	white-space: -moz-pre-wrap;
	white-space: -pre-wrap;
	white-space: -o-pre-wrap;
	word-wrap: break-word;
}

.plot-digit {
	display: inline;
	cursor: pointer;
}

.panel-plot {
	max-width: var(--sgnl-container-480);
}

.tab-badge-dot {
	width: var(--sgnl-icon-8);
	height: var(--sgnl-icon-8);
	border-radius: var(--sgnl-border-radius-4);
	background-color: var(--sgnl-alert-error-background);
	flex-shrink: 0;
}

.plot-digit-icon {
	color: var(--sgnl-brand-icon);
}

.plot-digit-value {
	color: var(--sgnl-brand-text);
}

.plot-digit-value-dotted {
	text-decoration: underline;
	text-decoration-style: dotted;
}

.panel-text {
	display: inline;
}
</style>

<style lang="scss">
.hover-overlay {
	position: fixed;
	z-index: 2501;
	pointer-events: none;
}

.hover-plot-body {
	width: var(--sgnl-container-480);
	min-height: var(--sgnl-container-343);
	display: flex;
	flex-direction: column;
	justify-content: center;
}
</style>
